/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import {
    FlexRow,
    FlexCol,
    Button,
    ExternalLink,
    useFlags,
    ContentBlock,
    Alert,
    Heading,
} from 'sarsaparilla';
import { PASS_IMG_PREFIX, ANNUAL_PASS_ID } from '../../constants';

const defaultProps = {
    govtShutdown: false,
};
const propTypes = {
    govtShutdown: PropTypes.bool,
};

export function InteragencyAnnualPassSection(props) {
    const {
        disableInteragencyPassSales,
        iaPassesGovShutdown,
        interagencyStopExternalSales,
    } = useFlags();

    const interagencyPassPurchaseTarget =
        disableInteragencyPassSales || iaPassesGovShutdown
            ? ''
            : `/pass/order-details/pass/${ANNUAL_PASS_ID}`;

    return (
        <ContentBlock
            className="pass-interagency-annual-wrapper py-6"
            id="interagency-annual-pass"
        >
            <FlexRow lg={12} md={12} id="skip-to-interagency">
                <FlexCol className="mb-2">
                    <Heading
                        headingLevel={2}
                        appearance="h4"
                        hasUnderline="left"
                        className="rec-section-heading-pass"
                    >
                        Interagency Passes
                    </Heading>
                </FlexCol>
            </FlexRow>
            {iaPassesGovShutdown && (
                <Alert
                    type="warning"
                    heading="Lapse in Funding"
                    headingLevel={3}
                    className="pass-interagency-annual-covid19-notification"
                    id="pass-landing-page-interagency-corona-virus-notification"
                >
                    <p>
                        Interagency Pass sales are not available during the lapse in
                        funding. We will resume pass sales after government operations
                        have resumed.
                    </p>
                </Alert>
            )}
            <FlexRow className="pass-interagency-annual-container">
                {!interagencyStopExternalSales && (
                    <FlexCol md={12} lg={6} className="pass-interagency-annual-body">
                        <FlexRow>
                            <FlexCol xs={12} className="pass-interagency-annual-content">
                                <p>
                                    This physical pass provides access to more than 2,000
                                    federal recreation sites. The Interagency Annual Pass
                                    is valid for a full year through the month of
                                    purchase. This pass does not provide discounts for
                                    camping, permit, or ticket reservations. The
                                    Interagency Annual Pass is shipped in the mail and
                                    must be carried with you; please allow time for
                                    shipping. Anyone 16 years or older may purchase the
                                    pass. Children 15 years and under are always admitted
                                    for free.
                                </p>
                                <p>
                                    <strong>Note:</strong> Discounted or free alternative
                                    interagency pass options are also available for
                                    certain qualifying individuals. You can learn more
                                    about alternative pass options through the&nbsp;
                                    <ExternalLink
                                        url="https://store.usgs.gov/recreational-passes"
                                        altText="Visit USGS for Alternative Passes"
                                    >
                                        USGS Online Store
                                    </ExternalLink>
                                </p>
                            </FlexCol>
                            <FlexCol>
                                <Button
                                    isDisabled={
                                        props.govtShutdown ||
                                        disableInteragencyPassSales ||
                                        iaPassesGovShutdown
                                    }
                                    href={interagencyPassPurchaseTarget}
                                    size="lg"
                                    title="Buy an America the Beautiful Annual Pass"
                                    aria-label="Buy an America the Beautiful Annual Pass"
                                >
                                    Buy Now
                                </Button>
                            </FlexCol>
                        </FlexRow>
                    </FlexCol>
                )}
                {interagencyStopExternalSales && (
                    <FlexCol md={12} lg={6} className="pass-interagency-annual-body">
                        <FlexRow>
                            <FlexCol xs={12} className="pass-interagency-annual-content">
                                <p>
                                    America the Beautiful - The National Parks and Federal
                                    Recreational Lands Passes are a suite of annual and
                                    lifetime passes that provides U.S. citizens or
                                    visitors an affordable and convenient way to access
                                    Federal recreational lands.
                                </p>
                                <p>
                                    Each pass covers entrance fees at lands managed by the
                                    National Park Service and US Fish and Wildlife
                                    Service, and standard amenity (day use) fees at lands
                                    managed by the Forest Service, Bureau of Land
                                    Management, Bureau of Reclamation, and US Army Corps
                                    of Engineers. A pass covers the pass owner and all
                                    occupants in a personal vehicle at sites that charge
                                    per vehicle, or the pass owner and up to three
                                    additional adults (16 and over) at sites that charge
                                    per person. Children ages 15 and under are admitted
                                    free.
                                </p>
                                <p>
                                    The Interagency Annual Pass is available to anyone and
                                    is valid for a full year through the month of
                                    purchase. This pass does not provide discounts for
                                    camping, permit, or ticket reservations. Alternative
                                    pass options are available for certain qualifying
                                    individuals. These affordable or free passes include
                                    options for seniors age 62 or older (Senior-Lifetime
                                    Pass or Senior-Annual Pass); 4th grade students (4th
                                    Grade Pass); current military and dependents
                                    (Military-Annual Pass), Gold Star families and
                                    veterans (Military-Lifetime Pass); individuals with
                                    permanent disabilities (Access Pass); and volunteers
                                    with more than 250 hours (Volunteer Pass).
                                </p>
                                <p>
                                    If you would like an interagency pass mailed to you
                                    before your trip, please visit the&nbsp;
                                    <ExternalLink
                                        url="https://store.usgs.gov"
                                        altText="Visit USGS store"
                                    >
                                        USGS Online Store
                                    </ExternalLink>
                                    &nbsp; to explore and select your pass options. Please
                                    consider the start date of your trip and factor in a
                                    minimum of 3 weeks to allow for processing and
                                    shipment of the pass.
                                </p>
                                <p>
                                    If you would like to get your pass in-person, please
                                    search the list of &nbsp;
                                    <ExternalLink
                                        url="https://www.nps.gov/planyourvisit/pickup-pass-locations.htm"
                                        altText="Places to Get Interagency Passes."
                                    >
                                        Places to Get Interagency Passes.
                                    </ExternalLink>
                                    &nbsp;
                                </p>
                            </FlexCol>
                        </FlexRow>
                    </FlexCol>
                )}
                <FlexCol md={12} lg={6} className="pass-interagency-annual-right-rail">
                    <div className="pass-interagency-annual-atb-image-wrap">
                        <img
                            className="pass-interagency-annual-atb-image"
                            src={`${PASS_IMG_PREFIX}2023/interagency-passes-graphic.png`}
                            alt="Interagency Annual Pass"
                            aria-label="Interagency Annual Pass"
                            loading="lazy"
                        />
                    </div>
                </FlexCol>
            </FlexRow>
        </ContentBlock>
    );
}

InteragencyAnnualPassSection.propTypes = propTypes;
InteragencyAnnualPassSection.defaultProps = defaultProps;

export default InteragencyAnnualPassSection;
